import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, BookOpen, Feather, StickyNote, Quote } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { HomeCollectionBanner } from "../components/HomeCollectionBanner";
import { useSeo } from "../lib/seo";
import { Faq } from "../components/Faq";

export default function Home() {
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [email, setEmail] = useState("");
  const [devotional, setDevotional] = useState(null);
  useSeo({
    title: null,
    description: "Bold, faith-filled digital downloads: Christian ebooks, guided prayer journals, scripture stickers and Bible studies. Instant delivery, weekly devotionals every Sunday.",
    path: "/",
    jsonLd: { "@context": "https://schema.org", "@type": "WebSite", name: "Faithful Store", url: window.location.origin, potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/shop?q={search_term_string}`, "query-input": "required name=search_term_string" } },
  });

  useEffect(() => {
    api.get("/storefront").then((r) => setConfig(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/devotional/current").then((r) => setDevotional(r.data?.id ? r.data : null));
  }, []);

  const featured = config?.featured_ids?.length
    ? products.filter((p) => config.featured_ids.includes(p.id))
    : products.slice(0, 3);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/subscribe", { email });
      toast.success("Welcome to the family. Check your inbox for a hello.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a192f] text-white">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url(${config?.hero_image || "https://images.unsplash.com/photo-1604882737206-8a000c03d8fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a192f] via-[#0a192f]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="overline text-[#d4af37] flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Christian Lifestyle · Est. 2026
            </div>
            <h1 className="font-serif mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {config?.hero_title || "Deep Faith. Bold Living."}
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
              {config?.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-shop-cta" to="/shop" className="btn-gold inline-flex items-center gap-2">
                Explore the Store <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/verse" className="btn-ghost text-white border-white/40 hover:bg-white hover:text-[#0a192f]">Today's Word</Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block fade-up d2">
            <div className="border border-[#d4af37]/30 bg-black/30 backdrop-blur-sm p-10">
              <Quote className="w-8 h-8 text-[#d4af37]" />
              <p className="font-serif text-2xl leading-snug mt-4 italic">
                {config?.verse_text}
              </p>
              <div className="mt-6 overline text-[#d4af37]">{config?.verse_reference}</div>
              <Link to="/verse" data-testid="hero-verse-link" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-white hover:text-[#d4af37]">Read today's deep dive <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-4 items-end mb-12">
          <div className="md:col-span-2">
            <div className="overline text-[#d4af37]">Curated Collections</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 text-[#0a192f]">
              Everything you need for the journey.
            </h2>
          </div>
          <Link to="/shop" data-testid="see-all-link" className="justify-self-end text-sm font-semibold text-[#0a192f] hover:text-[#d4af37] inline-flex items-center gap-2">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <CatCard testId="cat-ebooks" cat="ebook" title="Ebooks & Studies" desc="PDF, EPUB, HTML — deep-dive scripture, guidance and life-tested wisdom." icon={BookOpen} className="md:col-span-3 md:row-span-2 min-h-[380px]" img="https://images.unsplash.com/photo-1604882737206-8a000c03d8fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" />
          <CatCard testId="cat-journals" cat="journal" title="Journals" desc="Prayer prompts, gratitude pages, and space to hear Him clearly." icon={Feather} className="md:col-span-3" img="https://images.unsplash.com/photo-1775129667571-7fa3f51c05e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" />
          <CatCard testId="cat-stickers" cat="sticker" title="Stickers & Prints" desc="Small daily reminders of a bigger promise." icon={StickyNote} className="md:col-span-3" img="https://images.unsplash.com/photo-1558478551-be297c7bb253?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" />
        </div>
      </section>

      {/* SEASONAL COLLECTION */}
      <HomeCollectionBanner />

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
          <div className="mb-10">
            <div className="overline text-[#d4af37]">Featured</div>
            <h2 className="font-serif text-4xl mt-3 text-[#0a192f]">This week we're loving</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p) => (
              <Link data-testid={`featured-${p.id}`} key={p.id} to={`/product/${p.id}`} className="prod-card card-flat group block">
                <div className="aspect-[4/5] bg-[#f4f5f7] overflow-hidden">
                  {p.cover_key ? (
                    <img src={fileUrl(p.cover_key)} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#4a4a4a] font-serif text-2xl">{p.name?.[0]}</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="overline text-[#4a4a4a]">{p.category}</div>
                  <div className="font-serif text-2xl text-[#0a192f] mt-2 leading-tight">{p.name}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="font-semibold text-[#0a192f]">${(p.price_cents / 100).toFixed(2)}</div>
                    <span className="text-[#d4af37] text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      View <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* THIS WEEK'S DEVOTIONAL */}
      {devotional && (
        <section id="devotional" data-testid="devotional-section" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-10 border-l-4 border-[#d4af37] pl-8">
            <div>
              <div className="overline text-[#4a4a4a]">This week's devotional</div>
              <h2 className="font-serif text-4xl text-[#0a192f] mt-3 leading-tight" data-testid="devotional-title">{devotional.subject}</h2>
              <div className="text-xs text-[#4a4a4a] mt-4">{new Date(devotional.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
              <Link to="/devotionals" data-testid="devotional-archive-link" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0a192f] hover:text-[#d4af37]">Read past devotionals <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="text-[#1a1a1a] leading-relaxed space-y-5 text-base" data-testid="devotional-body-text">
              {devotional.body_text.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </section>
      )}

      <Faq />

      {/* VERSE + SUBSCRIBE */}
      <section id="verse" className="bg-[#0a192f] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="overline text-[#d4af37]">Verse of the day</div>
            <p className="font-serif text-4xl leading-snug mt-6 italic">
              {config?.verse_text}
            </p>
            <div className="divider-gold my-8" />
            <div className="overline text-[#d4af37]">{config?.verse_reference}</div>
          </div>
          <div id="about">
            <div className="overline text-[#d4af37]">Weekly Devotional</div>
            <h3 className="font-serif text-4xl mt-4">Encouragement, straight to your inbox.</h3>
            <p className="mt-5 text-white/70 leading-relaxed">
              Every week, a short devotional, a new product spotlight, and a fresh reminder that
              you were made for more than surviving your Monday.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="subscribe-email"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/5 border border-white/20 focus:border-[#d4af37] outline-none px-4 py-3 text-white placeholder:text-white/40"
              />
              <button data-testid="subscribe-submit" className="btn-gold">Subscribe</button>
            </form>
            <p className="text-xs text-white/40 mt-3">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function CatCard({ title, desc, icon: Icon, className = "", img, cat, testId }) {
  return (
    <Link
      data-testid={testId}
      to={`/shop?cat=${cat}`}
      className={`relative overflow-hidden card-flat group ${className}`}
    >
      <div className="absolute inset-0">
        <img src={img} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f]/95 via-[#0a192f]/60 to-transparent" />
      </div>
      <div className="relative p-8 h-full flex flex-col justify-end min-h-[240px] text-white">
        <Icon className="w-6 h-6 text-[#d4af37] mb-3" />
        <div className="font-serif text-3xl leading-tight">{title}</div>
        <div className="text-sm text-white/75 mt-2 max-w-md">{desc}</div>
        <div className="mt-4 text-[#d4af37] text-sm font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all">
          Shop {title.split(" ")[0]} <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
